import React, { useState, useEffect } from 'react';
import {
  Grid,
  TextField,
  Typography,
  Paper,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Box,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card,
  CardContent,
  Divider,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Field, type FormikErrors, type FormikTouched } from 'formik';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import { Button } from '../../../../shared/components/ui/Button';
import { useQuery } from '@tanstack/react-query';
import { userService } from '../../../../shared/services/users/usersServices';
import { roomService } from '../../../../shared/services/rooms/roomsServices';
import { reservationService } from '../../../../shared/services/reservation/reservationService';
import { useTenant } from '../../../../shared/context/TenantContext';
import { useAuth } from '../../../auth/context/AuthContext';

dayjs.locale('pt-br');

interface FormContentProps {
  values: any;
  errors: FormikErrors<any>;
  touched: FormikTouched<any>;
  isSubmitting: boolean;
  setFieldValue: (field: string, value: any) => void;
  onSubmit: (e?: React.FormEvent) => void;
}

interface AvailabilitySlot {
  room_id: string;
  room_name: string;
  request_date: string;
  initHour: string;
  endHour: string;
  available: boolean;
  is_reserved: boolean;
}

export function FormContent({
  values,
  errors,
  touched,
  isSubmitting,
  setFieldValue,
  onSubmit,
}: FormContentProps) {
  const { selectedTenant, handleSetSelectedTenant } = useTenant();
  const { user: authUser } = useAuth();
  const [availabilitySlots, setAvailabilitySlots] = useState<AvailabilitySlot[]>([]);
  const [checkingAvailability, setCheckingAvailability] = useState(false);

  useEffect(() => {
    if (selectedTenant && !values.tenant_id) {
      const cleanTenantId = selectedTenant.trim();
      setFieldValue('tenant_id', cleanTenantId);
    }
  }, [selectedTenant, values.tenant_id, setFieldValue]);

  const { data: usersData = [] } = useQuery({
    queryKey: ['users', 'condomino'],
    queryFn: async () => {
      const response = await userService.list({ type_flag: 'condomino' });
      return response.data || [];
    }
  });

  // Query para buscar rooms (áreas)
  const { data: roomsData = [] } = useQuery({
    queryKey: ['rooms'],
    queryFn: async () => {
      const response = await roomService.list();
      return response.data || [];
    }
  });

  const userOptions = usersData.map((user: any) => ({
    value: user.id,
    label: user.name,
  }));

  const roomOptions = roomsData.map((room: any) => ({
    value: room.id,
    label: room.name,
  }));

  useEffect(() => {
    const checkAvailabilityAsync = async () => {
      if (values.room_id && values.start_date) {
        setCheckingAvailability(true);
        try {
          const date = dayjs(values.start_date).format('YYYY-MM-DD');
          const response = await reservationService.checkAvailability(date, values.room_id);

          if (Array.isArray(response)) {
            setAvailabilitySlots(response);
          } else {
            setAvailabilitySlots([]);
          }
        } catch (error) {
          console.error('Erro ao verificar disponibilidade:', error);
          setAvailabilitySlots([]);
        } finally {
          setCheckingAvailability(false);
        }
      } else {
        setAvailabilitySlots([]);
      }
    };

    checkAvailabilityAsync();
  }, [values.room_id, values.start_date]);

  useEffect(() => {
    if (!values.name && values.start_date) {
      const timestamp = Math.floor(Date.now() / 1000);
      setFieldValue('name', `#RESERVA_${timestamp}`);
    }
  }, [values.start_date, values.name, setFieldValue]);

  const timeToMinutes = (time: string) => {
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
  };

  const isTimeSlotAvailable = () => {
    if (!values.start_date || !values.end_date || availabilitySlots.length === 0) {
      return true;
    }

    const startTime = dayjs(values.start_date).format('HH:mm');
    const endTime = dayjs(values.end_date).format('HH:mm');

    const startMinutes = timeToMinutes(startTime);
    const endMinutes = timeToMinutes(endTime);

    const availableSlots = availabilitySlots.filter(slot => slot.available && !slot.is_reserved);
    const sortedSlots = availableSlots.sort((a, b) => timeToMinutes(a.initHour) - timeToMinutes(b.initHour));

    let currentMinutes = startMinutes;

    for (const slot of sortedSlots) {
      const slotStartMinutes = timeToMinutes(slot.initHour);
      const slotEndMinutes = timeToMinutes(slot.endHour);

      if (slotStartMinutes <= currentMinutes && slotEndMinutes > currentMinutes) {
        currentMinutes = slotEndMinutes;
      }

      if (currentMinutes >= endMinutes) {
        return true;
      }
    }

    return false;
  };

  const getAvailabilityStats = () => {
    if (availabilitySlots.length === 0) return null;

    const totalSlots = availabilitySlots.length;
    const availableSlots = availabilitySlots.filter(slot => slot.available && !slot.is_reserved).length;
    const reservedSlots = availabilitySlots.filter(slot => slot.is_reserved).length;

    return {
      total: totalSlots,
      available: availableSlots,
      reserved: reservedSlots,
      percentage: Math.round((availableSlots / totalSlots) * 100)
    };
  };

  const groupSlotsByPeriod = (slots: AvailabilitySlot[]) => {
    const periods = {
      morning: { label: '🌅 Manhã (06:00 - 11:59)', slots: [] as AvailabilitySlot[] },
      afternoon: { label: '☀️ Tarde (12:00 - 17:59)', slots: [] as AvailabilitySlot[] },
      evening: { label: '🌙 Noite (18:00 - 23:59)', slots: [] as AvailabilitySlot[] },
      dawn: { label: '🌌 Madrugada (00:00 - 05:59)', slots: [] as AvailabilitySlot[] }
    };

    slots.forEach(slot => {
      const hour = parseInt(slot.initHour.split(':')[0]);

      if (hour >= 6 && hour < 12) {
        periods.morning.slots.push(slot);
      } else if (hour >= 12 && hour < 18) {
        periods.afternoon.slots.push(slot);
      } else if (hour >= 18 && hour < 24) {
        periods.evening.slots.push(slot);
      } else {
        periods.dawn.slots.push(slot);
      }
    });

    return periods;
  };

  const stats = getAvailabilityStats();
  const timeSlotAvailable = isTimeSlotAvailable();

  const availableSlots = availabilitySlots.filter(slot => slot.available && !slot.is_reserved);
  const reservedSlots = availabilitySlots.filter(slot => slot.is_reserved);

  const availablePeriods = groupSlotsByPeriod(availableSlots);
  const reservedPeriods = groupSlotsByPeriod(reservedSlots);

  // Função para selecionar um slot específico
  const selectSlot = (slot: AvailabilitySlot) => {
    const startDate = dayjs(values.start_date)
      .hour(parseInt(slot.initHour.split(':')[0]))
      .minute(parseInt(slot.initHour.split(':')[1]));

    const endDate = dayjs(values.start_date)
      .hour(parseInt(slot.endHour.split(':')[0]))
      .minute(parseInt(slot.endHour.split(':')[1]));

    setFieldValue('start_date', startDate.toISOString());
    setFieldValue('end_date', endDate.toISOString());
  };

  return (
    <LocalizationProvider
      dateAdapter={AdapterDayjs}
      adapterLocale="pt-br"
      localeText={{
        fieldDayPlaceholder: () => 'DD',
        fieldMonthPlaceholder: () => 'MM',
        fieldYearPlaceholder: () => 'AAAA',
        fieldHoursPlaceholder: () => 'HH',
        fieldMinutesPlaceholder: () => 'mm',
      }}
    >
      <Paper sx={{ p: 3 }}>
        <Typography variant="h4" fontWeight={600} gutterBottom>
          {values.id ? 'Editar Reserva' : 'Nova Reserva'}
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Field name="name">
              {({ field, meta }: any) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Nome da Reserva"
                  required
                  disabled={isSubmitting}
                  error={!!(meta.touched && meta.error)}
                  helperText={meta.touched && meta.error}
                  placeholder="Ex: #RESERVA_1684047236"
                />
              )}
            </Field>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Field name="room_id">
              {({ field, meta }: any) => (
                <FormControl
                  fullWidth
                  error={!!(meta.touched && meta.error)}
                  disabled={isSubmitting}
                >
                  <InputLabel required>Área/Sala</InputLabel>
                  <Select
                    {...field}
                    label="Área/Sala"
                  >
                    {roomOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {meta.touched && meta.error && (
                    <FormHelperText>{meta.error}</FormHelperText>
                  )}
                </FormControl>
              )}
            </Field>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Field name="user_id">
              {({ field, meta }: any) => (
                <FormControl
                  fullWidth
                  error={!!(meta.touched && meta.error)}
                  disabled={isSubmitting}
                >
                  <InputLabel required>Responsável</InputLabel>
                  <Select
                    {...field}
                    label="Responsável"
                  >
                    {userOptions.map((option: { value: string; label: string }) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {meta.touched && meta.error && (
                    <FormHelperText>{meta.error}</FormHelperText>
                  )}
                </FormControl>
              )}
            </Field>
          </Grid>

          <Grid item xs={12} sm={6}>
            <DateTimePicker
              label="Data/Hora de Início"
              value={values.start_date ? dayjs(values.start_date) : null}
              onChange={(newValue) => setFieldValue('start_date', newValue?.toISOString())}
              disabled={isSubmitting}
              format="DD/MM/YYYY HH:mm"
              ampm={false}
              minDateTime={dayjs()}
              slotProps={{
                textField: {
                  fullWidth: true,
                  required: true,
                  error: !!(touched.start_date && errors.start_date),
                  helperText: touched.start_date && typeof errors.start_date === 'string' ? errors.start_date : undefined,
                  placeholder: "DD/MM/AAAA HH:mm"
                },
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <DateTimePicker
              label="Data/Hora de Fim"
              value={values.end_date ? dayjs(values.end_date) : null}
              onChange={(newValue) => setFieldValue('end_date', newValue?.toISOString())}
              disabled={isSubmitting}
              format="DD/MM/YYYY HH:mm"
              ampm={false}
              minDateTime={values.start_date ? dayjs(values.start_date).add(10, 'minute') : dayjs()}
              slotProps={{
                textField: {
                  fullWidth: true,
                  required: true,
                  error: !!(touched.end_date && errors.end_date),
                  helperText: touched.end_date && typeof errors.end_date === 'string' ? errors.end_date : undefined,
                  placeholder: "DD/MM/AAAA HH:mm"
                },
              }}
            />
          </Grid>

       

          {/* Verificação de Disponibilidade */}
          {values.room_id && values.start_date && (
            <Grid item xs={12}>
              <Card sx={{ border: '1px solid', borderColor: 'divider' }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    📅 Horários Disponíveis para Reserva
                  </Typography>

                  {checkingAvailability ? (
                    <Box sx={{ textAlign: 'center', py: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Verificando disponibilidade...
                      </Typography>
                    </Box>
                  ) : availabilitySlots.length > 0 ? (
                    <Box>
                      {/* Informações da sala e data */}
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="body1" gutterBottom>
                          <strong>Área:</strong> {availabilitySlots[0]?.room_name}
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                          <strong>Data:</strong> {dayjs(availabilitySlots[0]?.request_date).format('DD/MM/YYYY')}
                        </Typography>
                      </Box>

                      {/* Estatísticas de disponibilidade */}
                      {stats && (
                        <Box sx={{ mb: 3 }}>
                          <Typography variant="subtitle2" gutterBottom>
                            Resumo da Disponibilidade:
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            <Chip
                              label={`${stats.available} horários livres`}
                              color="success"
                              size="small"
                            />
                            <Chip
                              label={`${stats.reserved} horários ocupados`}
                              color="error"
                              size="small"
                            />
                            <Chip
                              label={`${stats.percentage}% disponível`}
                              color={stats.percentage > 50 ? 'success' : 'warning'}
                              size="small"
                            />
                          </Box>
                        </Box>
                      )}

                      {/* Status do horário selecionado */}
                      {values.start_date && values.end_date && (
                        <Alert
                          severity={timeSlotAvailable ? 'success' : 'error'}
                          sx={{ mb: 3 }}
                        >
                          {timeSlotAvailable ? (
                            <>
                              ✅ <strong>Horário disponível!</strong> Você pode criar a reserva para o período das{' '}
                              {dayjs(values.start_date).format('HH:mm')} às{' '}
                              {dayjs(values.end_date).format('HH:mm')}.
                            </>
                          ) : (
                            <>
                              ❌ <strong>Horário não disponível!</strong> O período solicitado das{' '}
                              {dayjs(values.start_date).format('HH:mm')} às{' '}
                              {dayjs(values.end_date).format('HH:mm')} não possui cobertura completa ou contínua.
                            </>
                          )}
                        </Alert>
                      )}

                      {/* Horários disponíveis organizados por período */}
                      {availableSlots.length > 0 && (
                        <Accordion sx={{ mb: 2 }} defaultExpanded>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="subtitle1" color="success.main" fontWeight={600}>
                              🟢 Horários Disponíveis ({stats?.available} slots de 10 minutos)
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            {Object.entries(availablePeriods).map(([key, period]) => (
                              period.slots.length > 0 && (
                                <Box key={key} sx={{ mb: 3 }}>
                                  <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                                    {period.label} ({period.slots.length} slots)
                                  </Typography>
                                  <Grid container spacing={1}>
                                    {period.slots.map((slot, index) => (
                                      <Grid item key={index}>
                                        <Chip
                                          label={`${slot.initHour} - ${slot.endHour}`}
                                          color="success"
                                          variant="outlined"
                                          size="small"
                                          clickable
                                          onClick={() => selectSlot(slot)}
                                          sx={{
                                            fontFamily: 'monospace',
                                            fontSize: '0.75rem',
                                            '&:hover': {
                                              backgroundColor: 'success.light',
                                              color: 'success.contrastText'
                                            }
                                          }}
                                        />
                                      </Grid>
                                    ))}
                                  </Grid>
                                  {key !== 'dawn' && <Divider sx={{ mt: 2 }} />}
                                </Box>
                              )
                            ))}

                            <Box sx={{ mt: 2, p: 2, bgcolor: 'success.light', borderRadius: 1, alpha: 0.1 }}>
                              <Typography variant="caption" color="success.dark">
                                💡 <strong>Dica:</strong> Clique em qualquer horário disponível para selecioná-lo automaticamente nos campos acima.
                                Cada slot representa um período de 10 minutos que pode ser reservado.
                              </Typography>
                            </Box>
                          </AccordionDetails>
                        </Accordion>
                      )}

                      {/* Horários ocupados organizados por período */}
                      {reservedSlots.length > 0 && (
                        <Accordion>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="subtitle1" color="error.main" fontWeight={600}>
                              🔴 Horários Ocupados ({stats?.reserved} slots)
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            {Object.entries(reservedPeriods).map(([key, period]) => (
                              period.slots.length > 0 && (
                                <Box key={key} sx={{ mb: 3 }}>
                                  <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                                    {period.label} ({period.slots.length} slots)
                                  </Typography>
                                  <Grid container spacing={1}>
                                    {period.slots.map((slot, index) => (
                                      <Grid item key={index}>
                                        <Chip
                                          label={`${slot.initHour} - ${slot.endHour}`}
                                          color="error"
                                          variant="outlined"
                                          size="small"
                                          sx={{
                                            fontFamily: 'monospace',
                                            fontSize: '0.75rem'
                                          }}
                                        />
                                      </Grid>
                                    ))}
                                  </Grid>
                                  {key !== 'dawn' && <Divider sx={{ mt: 2 }} />}
                                </Box>
                              )
                            ))}
                          </AccordionDetails>
                        </Accordion>
                      )}
                    </Box>
                  ) : (
                    <Alert severity="warning">
                      Nenhum horário disponível encontrado para esta data. Tente uma data diferente.
                    </Alert>
                  )}
                </CardContent>
              </Card>
            </Grid>
          )}

          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                onClick={() => window.history.back()}
                disabled={isSubmitting}
              >
                Cancelar
              </Button>
              <Button
                variant="contained"
                type="submit"
                disabled={isSubmitting || (values.start_date && values.end_date && !timeSlotAvailable)}
                loading={isSubmitting}
              >
                {values.id ? 'Atualizar Reserva' : 'Criar Reserva'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </LocalizationProvider>
  );
}